import React, { useCallback, useEffect, useState } from 'react';
import { API_BASE_URL } from '../../utils/apiBase';

type ReviewSettings = {
  googleReviewUrl: string;
  envGoogleReviewUrl: string;
  effectiveGoogleReviewUrl: string;
  source: 'admin' | 'env' | 'default' | 'none';
  placeholder: string;
  configured: boolean;
};

type ReviewRow = {
  quoteId: string;
  clientName?: string;
  clientEmail?: string;
  quoteNumber?: string;
  shootType?: string;
  location?: string;
  adHoc?: boolean;
  reviewRequestSentAt?: string;
  lastSentAt?: string;
  reminderSentAt?: string;
  reviewReceived?: boolean;
  reviewReceivedAt?: string;
  optedOut?: boolean;
};

function formatWhen(value?: string) {
  if (!value) return '—';
  const ms = Date.parse(value);
  if (!Number.isFinite(ms)) return value;
  return new Intl.DateTimeFormat('en-ZA', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(ms));
}

const ReviewRequests = ({
  onOpenShoot,
}: {
  onOpenShoot?: (quoteId: string) => void;
}) => {
  const [settings, setSettings] = useState<ReviewSettings | null>(null);
  const [urlDraft, setUrlDraft] = useState('');
  const [rows, setRows] = useState<ReviewRow[]>([]);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [adhocName, setAdhocName] = useState('');
  const [adhocEmail, setAdhocEmail] = useState('');
  const [adhocSending, setAdhocSending] = useState(false);
  const [copyNote, setCopyNote] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const response = await fetch(`${API_BASE_URL}/studio/review-requests`);
      if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        throw new Error(body.error || 'Could not load review requests.');
      }
      const body = await response.json();
      setRows(Array.isArray(body.requests) ? body.requests : []);
      if (body.settings) {
        setSettings(body.settings);
        setUrlDraft(body.settings.googleReviewUrl || '');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not load review requests.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load().catch(() => undefined);
  }, [load]);

  const saveSettings = async () => {
    setSaving(true);
    setError('');
    setMessage('');
    try {
      const response = await fetch(`${API_BASE_URL}/studio/review-settings`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ googleReviewUrl: urlDraft.trim() }),
      });
      const body = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(body.error || 'Could not save the review link.');
      setSettings(body);
      setUrlDraft(body.googleReviewUrl || '');
      setMessage(body.configured ? 'Google review link saved.' : 'Review link cleared. Automatic emails stay off until a Place ID is set.');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save the review link.');
    } finally {
      setSaving(false);
    }
  };

  const copyReviewLink = async () => {
    const link = settings?.effectiveGoogleReviewUrl || '';
    setCopyNote('');
    setError('');
    if (!link) {
      setError('Save a Google review link first.');
      return;
    }
    try {
      await navigator.clipboard.writeText(link);
      setCopyNote('Review link copied. You can paste it into WhatsApp, SMS, or email.');
    } catch (_) {
      setError('Could not copy the link. Select it from the Review URL field instead.');
    }
  };

  const sendAdHoc = async (event: React.FormEvent) => {
    event.preventDefault();
    setAdhocSending(true);
    setError('');
    setMessage('');
    try {
      const response = await fetch(`${API_BASE_URL}/studio/review-requests/adhoc`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: adhocEmail.trim(),
          name: adhocName.trim(),
        }),
      });
      const body = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(body.error || 'Could not send the review request.');
      setMessage(`Review request sent to ${adhocEmail.trim()}.`);
      setAdhocEmail('');
      setAdhocName('');
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not send the review request.');
    } finally {
      setAdhocSending(false);
    }
  };

  const toggleReceived = async (row: ReviewRow, next: boolean) => {
    setError('');
    try {
      const response = await fetch(`${API_BASE_URL}/studio/quotes/${encodeURIComponent(row.quoteId)}/review-request`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ reviewReceived: next }),
      });
      const body = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(body.error || 'Could not update review status.');
      setRows((current) => current.map((item) => (
        item.quoteId === row.quoteId
          ? { ...item, reviewReceived: body.reviewReceived, reviewReceivedAt: body.reviewReceivedAt }
          : item
      )));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not update review status.');
    }
  };

  const sourceNote = !settings
    ? ''
    : settings.source === 'admin'
      ? 'Using the link saved here.'
      : settings.source === 'env'
        ? 'Using GOOGLE_REVIEW_URL from the server environment.'
        : settings.source === 'default'
          ? 'Using the studio Google review link built into the app. Save link to keep it in Studio settings.'
          : 'No review link is set yet. Automatic and manual emails stay off.';

  const reviewReady = Boolean(settings?.configured && settings.effectiveGoogleReviewUrl);

  return (
    <div className="studio-home" data-testid="review-requests">
      <header className="studio-home-head">
        <div>
          <h2>Review requests</h2>
          <p>
            Thank-you emails after a shoot is complete and paid, plus ad-hoc asks when you want someone to leave a Google review.
          </p>
        </div>
      </header>

      <section className="studio-panel" aria-label="Google review link">
        <h3 className="studio-section-label">Google review link</h3>
        <p className="studio-note">
          Clients land on Google&apos;s review page. The studio review link is already filled in. Do not add tracking parameters.
        </p>
        <label>
          Review URL
          <input
            type="url"
            value={urlDraft}
            placeholder={settings?.placeholder || 'https://g.page/r/CVOjN3ADJabAEBM/review'}
            onChange={(event) => setUrlDraft(event.target.value)}
            aria-label="Google review URL"
          />
        </label>
        <p className="studio-note">{sourceNote}</p>
        {!settings?.configured ? (
          <p className="studio-note" role="status">Review emails are disabled until a valid https link is saved.</p>
        ) : null}
        <div className="studio-toolbar">
          <button type="button" className="studio-primary" onClick={saveSettings} disabled={saving}>
            {saving ? 'Saving…' : 'Save link'}
          </button>
          <button
            type="button"
            className="studio-secondary"
            onClick={copyReviewLink}
            disabled={!reviewReady}
          >
            Copy review link
          </button>
          <button type="button" className="studio-secondary" onClick={() => load()}>Refresh list</button>
        </div>
        {copyNote ? <p className="studio-note" role="status">{copyNote}</p> : null}
        {message ? <p className="studio-note" role="status">{message}</p> : null}
        {error ? <p className="studio-note" role="alert">{error}</p> : null}
      </section>

      <section className="studio-panel" aria-label="Ask someone for a review">
        <h3 className="studio-section-label">Ask someone for a review</h3>
        <p className="studio-note">
          Send the branded review email to any address, or copy the Google link above for WhatsApp / SMS.
          Copy lives under Messaging → Google review request.
        </p>
        <form className="studio-adhoc-review" onSubmit={sendAdHoc}>
          <label>
            Name (optional)
            <input
              type="text"
              value={adhocName}
              onChange={(event) => setAdhocName(event.target.value)}
              aria-label="Recipient name"
              autoComplete="name"
            />
          </label>
          <label>
            Email
            <input
              type="email"
              value={adhocEmail}
              onChange={(event) => setAdhocEmail(event.target.value)}
              aria-label="Recipient email"
              autoComplete="email"
              required
            />
          </label>
          <button
            type="submit"
            className="studio-primary"
            disabled={adhocSending || !reviewReady || !adhocEmail.trim()}
          >
            {adhocSending ? 'Sending…' : 'Send review email'}
          </button>
        </form>
      </section>

      <section className="studio-panel" aria-label="Sent review requests">
        <h3 className="studio-section-label">Sent / reminded / received</h3>
        {loading ? <p className="studio-empty">Loading…</p> : null}
        {!loading && rows.length === 0 ? (
          <p className="studio-empty">No review requests have been sent yet.</p>
        ) : null}
        {!loading && rows.length > 0 ? (
          <div className="studio-table-wrap">
            <table className="studio-table">
              <thead>
                <tr>
                  <th>Client</th>
                  <th>Shoot</th>
                  <th>Sent</th>
                  <th>Reminded</th>
                  <th>Received</th>
                  <th>Opted out</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => {
                  const label = row.clientName || row.clientEmail || 'Client';
                  const canOpenShoot = Boolean(onOpenShoot && !row.adHoc && row.quoteId && !String(row.quoteId).startsWith('adhoc-'));
                  return (
                    <tr
                      key={row.quoteId}
                      style={canOpenShoot ? { cursor: 'pointer' } : undefined}
                      onClick={canOpenShoot ? () => onOpenShoot?.(row.quoteId) : undefined}
                      data-testid={canOpenShoot ? `review-row-${row.quoteId}` : undefined}
                    >
                      <td>
                        {canOpenShoot ? (
                          <button
                            type="button"
                            className="studio-text-action"
                            onClick={(event) => {
                              event.stopPropagation();
                              onOpenShoot?.(row.quoteId);
                            }}
                          >
                            {label}
                          </button>
                        ) : label}
                        <div>
                          <small>{row.adHoc ? 'Ad hoc' : (row.quoteNumber || row.quoteId)}</small>
                          {row.clientEmail ? (
                            <div><small>{row.clientEmail}</small></div>
                          ) : null}
                        </div>
                      </td>
                      <td>
                        {row.adHoc
                          ? 'Ad hoc ask'
                          : ([row.shootType, row.location].filter(Boolean).join(' · ') || '—')}
                      </td>
                      <td>{formatWhen(row.reviewRequestSentAt || row.lastSentAt)}</td>
                      <td>{formatWhen(row.reminderSentAt)}</td>
                      <td onClick={(event) => event.stopPropagation()}>
                        {canOpenShoot ? (
                          row.reviewReceived ? 'Received' : '—'
                        ) : (
                          <label>
                            <input
                              type="checkbox"
                              checked={Boolean(row.reviewReceived)}
                              onChange={(event) => toggleReceived(row, event.target.checked)}
                              aria-label={`Review received for ${label}`}
                            />
                            {' '}Received
                          </label>
                        )}
                      </td>
                      <td>{row.optedOut ? 'Yes' : '—'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : null}
      </section>
    </div>
  );
};

export default ReviewRequests;
